/**
 * Beatswell Mega Menu Hover Behavior
 * Simple, clean implementation for hover-based mega menus
 */

(function () {
  'use strict';

  // Single source of truth
  let currentOpenDetails = null;
  let closeTimeout = null;
  let activeCloseAnimations = []; // Track running close animations so we can cancel them
  let isClosing = false; // Flag to detect re-hover during close animation

  // Animation config
  const ANIMATION_DURATION = 0.25;
  const CLOSE_DELAY = 300;
  const EASING = [0.4, 0, 0.2, 1];

  function waitForMotion(callback, maxAttempts = 50) {
    if (typeof Motion !== 'undefined' && typeof Motion.animate === 'function') {
      callback();
    } else if (maxAttempts > 0) {
      setTimeout(() => waitForMotion(callback, maxAttempts - 1), 50);
    } else {
      callback();
    }
  }

  function showMenuInstantly(details, content, summary) {
    if (!details || !content) return;

    details.setAttribute('open', '');
    if (summary) summary.setAttribute('aria-expanded', 'true');

    // Show instantly - no animation
    content.style.opacity = '1';
    content.style.transform = 'scaleY(1)';
    content.style.visibility = 'visible';
    content.style.pointerEvents = 'auto';

    const innerContent = content.querySelector('.mega-menu__container');
    if (innerContent) {
      innerContent.style.opacity = '1';
    }
  }

  function hideMenuInstantly(details, content, summary) {
    if (!details || !content) return;

    content.style.opacity = '1'; // Container doesn't fade
    content.style.transform = 'scaleY(0)';
    content.style.visibility = 'hidden';
    content.style.pointerEvents = 'none';

    const innerContent = content.querySelector('.mega-menu__container');
    if (innerContent) {
      innerContent.style.opacity = '0';
    }

    details.removeAttribute('open');
    if (summary) summary.setAttribute('aria-expanded', 'false');
  }

  /** Stop all running close animations and snap menu back to fully open */
  function abortCloseAnimations(details, content, summary) {
    activeCloseAnimations.forEach((anim) => {
      try {
        if (anim && typeof anim.cancel === 'function') anim.cancel();
      } catch (_) {}
    });
    activeCloseAnimations = [];
    isClosing = false;

    // Snap back to fully open state
    if (details && content) {
      showMenuInstantly(details, content, summary);
    }
  }

  function openMenu(details, content, summary) {
    if (!details || !content) return;

    // Cancel any pending close timeout
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      closeTimeout = null;
    }

    // If a close animation is in progress, abort it and snap open
    if (isClosing && currentOpenDetails === details) {
      abortCloseAnimations(details, content, summary);
      return;
    }

    // If same menu is already open, do nothing
    if (currentOpenDetails === details && details.hasAttribute('open')) {
      return;
    }

    // Check if we're switching from another menu
    const previousDetails = currentOpenDetails;
    const isSwitching = previousDetails !== null && previousDetails !== details;

    // FIRST: Show new menu instantly (before hiding old)
    if (isSwitching) {
      // Abort any close animation on the old menu
      if (isClosing) {
        abortCloseAnimations(previousDetails, previousDetails.querySelector('.mega-menu__content'), previousDetails.querySelector('summary'));
      }

      // Show new menu FIRST so there's no gap
      showMenuInstantly(details, content, summary);
      currentOpenDetails = details;

      // THEN hide old menu
      const oldContent = previousDetails.querySelector('.mega-menu__content');
      const oldSummary = previousDetails.querySelector('summary');
      hideMenuInstantly(previousDetails, oldContent, oldSummary);
      return;
    }

    // Opening fresh (not switching) - animate
    details.setAttribute('open', '');
    if (summary) summary.setAttribute('aria-expanded', 'true');
    currentOpenDetails = details;

    // Set initial state before animation
    content.style.opacity = '1'; // Container doesn't fade, only scales
    content.style.transform = 'scaleY(0)';
    content.style.visibility = 'visible';
    content.style.pointerEvents = 'auto';

    // Get inner content wrapper for fade animation
    const innerContent = content.querySelector('.mega-menu__container');
    if (innerContent) {
      innerContent.style.opacity = '0';
    }

    // Force reflow
    void content.offsetHeight;

    // Animate open - container scales first, then content fades
    if (typeof Motion !== 'undefined' && typeof Motion.animate === 'function') {
      try {
        // Animate container scale (grow) first
        const scaleAnimation = Motion.animate(
          content,
          {
            transform: ['scaleY(0)', 'scaleY(1)'],
          },
          { duration: ANIMATION_DURATION, easing: EASING }
        );

        // After scale completes, fade in content
        if (innerContent) {
          if (scaleAnimation && scaleAnimation.finished) {
            scaleAnimation.finished
              .then(() => {
                // Now fade in the content
                Motion.animate(
                  innerContent,
                  {
                    opacity: [0, 1],
                  },
                  { duration: ANIMATION_DURATION, easing: EASING }
                );
              })
              .catch(() => {
                innerContent.style.opacity = '1';
              });
          } else {
            // Fallback if finished promise not available
            setTimeout(() => {
              Motion.animate(
                innerContent,
                {
                  opacity: [0, 1],
                },
                { duration: ANIMATION_DURATION, easing: EASING }
              );
            }, ANIMATION_DURATION * 1000);
          }
        }
      } catch (e) {
        content.style.transform = 'scaleY(1)';
        if (innerContent) {
          innerContent.style.opacity = '1';
        }
      }
    } else {
      content.style.transform = 'scaleY(1)';
      if (innerContent) {
        innerContent.style.opacity = '1';
      }
    }

    // Add class to header
    const headerWrapper = document.querySelector('.header-wrapper');
    if (headerWrapper) {
      headerWrapper.classList.add('mega-menu-open');
    }
  }

  function closeMenu(details, content, summary, animate = true) {
    if (!details || !content) return;

    // Only close if this is the current open menu
    if (currentOpenDetails !== details) return;

    const innerContent = content.querySelector('.mega-menu__container');

    const doClose = () => {
      activeCloseAnimations = [];
      isClosing = false;

      content.style.opacity = '1'; // Container doesn't fade
      content.style.transform = 'scaleY(0)';
      content.style.visibility = 'hidden';
      content.style.pointerEvents = 'none';

      if (innerContent) {
        innerContent.style.opacity = '0';
      }

      details.removeAttribute('open');
      if (summary) summary.setAttribute('aria-expanded', 'false');

      if (currentOpenDetails === details) {
        currentOpenDetails = null;
      }

      // Remove hover class from header
      const headerWrapper = document.querySelector('.header-wrapper');
      if (headerWrapper && !document.querySelector('.mega-menu[open]')) {
        headerWrapper.classList.remove('mega-menu-open');
        if (!headerWrapper.matches(':hover')) {
          headerWrapper.classList.remove('header-hover');
        }
      }
    };

    if (animate && typeof Motion !== 'undefined' && typeof Motion.animate === 'function') {
      isClosing = true;
      activeCloseAnimations = [];

      try {
        // First fade out content, then scale down container
        if (innerContent) {
          const fadeAnimation = Motion.animate(
            innerContent,
            {
              opacity: [1, 0],
            },
            { duration: ANIMATION_DURATION, easing: EASING }
          );
          activeCloseAnimations.push(fadeAnimation);

          // After fade completes, scale down container
          if (fadeAnimation && fadeAnimation.finished) {
            fadeAnimation.finished
              .then(() => {
                if (!isClosing) return; // Aborted
                // Now scale down the container
                const scaleAnimation = Motion.animate(
                  content,
                  {
                    transform: ['scaleY(1)', 'scaleY(0)'],
                  },
                  { duration: ANIMATION_DURATION, easing: EASING }
                );
                activeCloseAnimations.push(scaleAnimation);

                if (scaleAnimation && scaleAnimation.finished) {
                  scaleAnimation.finished.then(() => {
                    if (isClosing) doClose();
                  }).catch(() => {
                    // Animation was cancelled — don't close
                  });
                } else {
                  setTimeout(() => {
                    if (isClosing) doClose();
                  }, ANIMATION_DURATION * 1000);
                }
              })
              .catch(() => {
                // Animation was cancelled — don't close
              });
          } else {
            // Fallback if finished promise not available
            setTimeout(() => {
              if (!isClosing) return;
              const scaleAnimation = Motion.animate(
                content,
                {
                  transform: ['scaleY(1)', 'scaleY(0)'],
                },
                { duration: ANIMATION_DURATION, easing: EASING }
              );
              activeCloseAnimations.push(scaleAnimation);
              setTimeout(() => {
                if (isClosing) doClose();
              }, ANIMATION_DURATION * 1000);
            }, ANIMATION_DURATION * 1000);
          }
        } else {
          // No inner content, just scale
          const scaleAnimation = Motion.animate(
            content,
            {
              transform: ['scaleY(1)', 'scaleY(0)'],
            },
            { duration: ANIMATION_DURATION, easing: EASING }
          );
          activeCloseAnimations.push(scaleAnimation);

          if (scaleAnimation && scaleAnimation.finished) {
            scaleAnimation.finished.then(() => {
              if (isClosing) doClose();
            }).catch(() => {
              // Cancelled
            });
          } else {
            setTimeout(() => {
              if (isClosing) doClose();
            }, ANIMATION_DURATION * 1000);
          }
        }
      } catch (e) {
        doClose();
      }
    } else {
      doClose();
    }
  }

  function scheduleClose(details, content, summary) {
    if (closeTimeout) {
      clearTimeout(closeTimeout);
    }

    closeTimeout = setTimeout(() => {
      closeTimeout = null;

      // Check if anything is still being hovered
      const menu = details.closest('header-menu');
      if (menu && (menu.matches(':hover') || content.matches(':hover'))) {
        return;
      }

      const anyMenuHovered = document.querySelector('header-menu:hover');
      if (anyMenuHovered) {
        return;
      }

      // Check if mouse is still within the header nav area (bridging gaps between items)
      const inlineMenu = document.querySelector('.header__inline-menu:hover');
      if (inlineMenu) {
        return;
      }

      closeMenu(details, content, summary, true);
    }, CLOSE_DELAY);
  }

  function cancelClose() {
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      closeTimeout = null;
    }

    // If a close animation is already running, abort it and snap back open
    if (isClosing && currentOpenDetails) {
      const content = currentOpenDetails.querySelector('.mega-menu__content');
      const summary = currentOpenDetails.querySelector('summary');
      abortCloseAnimations(currentOpenDetails, content, summary);
    }
  }

  function init() {
    const headerMenus = document.querySelectorAll('header-menu');

    // Safety net: close open menu when mouse leaves the entire nav area
    const inlineMenu = document.querySelector('.header__inline-menu');
    if (inlineMenu) {
      inlineMenu.addEventListener('mouseleave', () => {
        if (currentOpenDetails) {
          const content = currentOpenDetails.querySelector('.mega-menu__content');
          const summary = currentOpenDetails.querySelector('summary');
          // Only schedule close if mouse isn't hovering the dropdown content
          if (content && !content.matches(':hover')) {
            scheduleClose(currentOpenDetails, content, summary);
          }
        }
      });
    }

    headerMenus.forEach((menu) => {
      const details = menu.querySelector('details.mega-menu');
      if (!details) return;

      const summary = details.querySelector('summary');
      const content = details.querySelector('.mega-menu__content');
      if (!summary || !content) return;

      // On click: navigate to the link URL if available, otherwise just prevent toggle
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const href = summary.getAttribute('data-href');
        if (href && href !== '#') {
          window.location.href = href;
        }
      });

      // Open on hover
      menu.addEventListener('mouseenter', () => {
        cancelClose();
        openMenu(details, content, summary);
      });

      summary.addEventListener('mouseenter', () => {
        cancelClose();
        openMenu(details, content, summary);
      });

      content.addEventListener('mouseenter', () => {
        cancelClose();
      });

      // Close with delay on mouse leave
      menu.addEventListener('mouseleave', (e) => {
        if (content.contains(e.relatedTarget)) return;
        const nextMenu = e.relatedTarget?.closest('header-menu');
        if (nextMenu && nextMenu !== menu) return;
        // Stay open if mouse moved to the nav list area (gap between items)
        if (e.relatedTarget?.closest('.header__inline-menu')) return;
        scheduleClose(details, content, summary);
      });

      content.addEventListener('mouseleave', (e) => {
        if (menu.contains(e.relatedTarget)) return;
        const nextMenu = e.relatedTarget?.closest('header-menu');
        if (nextMenu && nextMenu !== menu) return;
        // Stay open if mouse moved back up to the nav area
        if (e.relatedTarget?.closest('.header__inline-menu')) return;
        scheduleClose(details, content, summary);
      });

      // Keyboard accessibility
      summary.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (details.hasAttribute('open')) {
            closeMenu(details, content, summary, true);
          } else {
            openMenu(details, content, summary);
          }
        } else if (e.key === 'Escape') {
          closeMenu(details, content, summary, true);
          summary.focus();
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => waitForMotion(init));
  } else {
    waitForMotion(init);
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => waitForMotion(init));
  }
})();
